import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    TextField,
    Checkbox,
    Chip,
    CircularProgress,
    Alert,
    Collapse,
    Divider,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HotelIcon from '@mui/icons-material/Hotel';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { API_BASE_URL } from '../config';

// ── Dark surface palette (matches EmailReviewModal) ──────────────────────────
const DARK_BG      = '#1a1d2e';
const DARK_SURFACE = '#242740';
const DARK_BORDER  = 'rgba(255,255,255,0.10)';
const ACCENT       = '#6C63FF';
const TEXT_PRIMARY = '#f0f0f5';
const TEXT_MUTED   = 'rgba(240,240,245,0.55)';

const fieldSx = {
    '& .MuiInputBase-root': { color: TEXT_PRIMARY, backgroundColor: 'rgba(255,255,255,0.03)', fontSize: '0.85rem' },
    '& .MuiInputBase-input': { padding: '8px 10px' },
    '& .MuiInputLabel-root': { color: TEXT_MUTED, fontSize: '0.8rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: DARK_BORDER },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
};

const PACE_OPTIONS = ['Relaxed', 'Moderate', 'Fast'];
const INTEREST_OPTIONS = ['Culture', 'Nature', 'Adventure', 'Food', 'Shopping', 'Relaxation', 'Nightlife', 'History', 'Art'];

const MODE_LABEL = {
    fixed:      { label: 'Fixed-date trip', color: '#34d399' },
    named_days: { label: 'Flexible trip · named days', color: '#60a5fa' },
    ai_paced:   { label: 'Flexible trip · AI-paced', color: '#c084fc' },
};

function typeIcon(item) {
    if (item.type === 'lodging') return <HotelIcon fontSize="small" sx={{ color: '#f59e0b' }} />;
    if (item.type === 'transportation') {
        return item.subtype === 'flight'
            ? <FlightIcon fontSize="small" sx={{ color: '#38bdf8' }} />
            : <DirectionsTransitIcon fontSize="small" sx={{ color: '#38bdf8' }} />;
    }
    return <LocalActivityIcon fontSize="small" sx={{ color: '#a78bfa' }} />;
}

/**
 * AI-Powered Unstructured Itinerary Importer modal.
 *
 * @param open          boolean
 * @param onClose       () => void
 * @param tripId        number|null — when provided, runs the In-Trip Append Flow
 * @param existingTripName  string — header label for append flow
 * @param onComplete    (tripId, createdNew) => void — called after a successful import
 */
export default function AiItineraryImportModal({ open, onClose, tripId = null, existingTripName = '', onComplete }) {
    const isAppend = !!tripId;

    const [stage, setStage]   = useState('input'); // input | loading | review | committing
    const [rawText, setRawText] = useState('');
    const [error, setError]   = useState('');
    const [progress, setProgress] = useState({ pct: 0, step: '', items: 0, list: [] });
    const [elapsed, setElapsed] = useState(0);

    // Tick an elapsed-seconds counter while the AI works, so the user always sees motion
    // even when a single step (e.g. planning) takes a while.
    useEffect(() => {
        if (stage !== 'loading') { setElapsed(0); return; }
        const started = Date.now();
        const id = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(id);
    }, [stage]);

    const fmtElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    const [result, setResult] = useState(null); // full parse payload
    const [items, setItems]   = useState([]);
    const [expanded, setExpanded] = useState({}); // id -> bool
    const fileRef = useRef(null);

    // Editable trip-profile fields (new-trip flow only)
    const [tripName, setTripName]   = useState('');
    const [destination, setDestination] = useState('');
    const [pace, setPace]           = useState('Moderate');
    const [interests, setInterests] = useState([]);

    const resetAll = useCallback(() => {
        setStage('input'); setRawText(''); setError(''); setResult(null);
        setItems([]); setExpanded({}); setTripName(''); setDestination('');
        setPace('Moderate'); setInterests([]); setProgress({ pct: 0, step: '', items: 0, list: [] });
    }, []);

    const handleClose = useCallback(() => {
        if (stage === 'loading' || stage === 'committing') return; // block close mid-flight
        resetAll();
        onClose && onClose();
    }, [stage, resetAll, onClose]);

    const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        try {
            const text = await file.text();
            setRawText(prev => (prev ? prev + '\n\n' : '') + text);
        } catch (err) {
            setError('Could not read that file. Please paste the text instead.');
        }
        if (fileRef.current) fileRef.current.value = '';
    };

    const applyParsed = (data) => {
        setResult(data);
        setItems(data.items);
        setTripName(data.trip_name || 'Imported Trip');
        setDestination(data.primary_destination || '');
        setPace(data.pace || 'Moderate');
        setInterests(Array.isArray(data.interests) ? data.interests : []);
        setStage('review');
    };

    // Parsing runs as a detached background job so a slow AI provider never trips
    // the Cloudflare ~100s edge timeout. Kick off the job, then poll for the result.
    const handleParse = async () => {
        if (rawText.trim().length < 3) {
            setError('Please paste or upload an itinerary first.');
            return;
        }
        setError('');
        setProgress({ pct: 0, step: 'Starting…', items: 0, list: [] });
        setStage('loading');
        const token = localStorage.getItem('token');

        let jobId;
        try {
            const resp = await fetch(`${API_BASE_URL}/ai/import_itinerary_parse_start.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ raw_text: rawText, trip_id: tripId || undefined }),
            });
            const data = await resp.json();
            if (!resp.ok || !data.success || !data.job_id) {
                setError(data.message || 'Could not start the import.');
                setStage('input');
                return;
            }
            jobId = data.job_id;
        } catch (err) {
            setError('Network error while contacting the AI service.');
            setStage('input');
            return;
        }

        // Poll the status endpoint until the job finishes (~2.5s cadence, ~5min cap).
        const maxAttempts = 120;
        const delay = (ms) => new Promise(r => setTimeout(r, ms));
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await delay(2500);
            try {
                const resp = await fetch(`${API_BASE_URL}/ai/import_itinerary_parse_status.php?job_id=${jobId}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                const data = await resp.json();
                if (data.status === 'completed' && data.success) {
                    if (!data.items || data.items.length === 0) {
                        setError('No travel items were found. Try adding more detail.');
                        setStage('input');
                        return;
                    }
                    applyParsed(data);
                    return;
                }
                if (data.status === 'failed' || (data.success === false && data.status !== 'pending' && data.status !== 'processing')) {
                    setError(data.message || 'Could not parse the itinerary.');
                    setStage('input');
                    return;
                }
                // still pending/processing — reflect live progress in the UI
                if (data.status === 'pending' || data.status === 'processing') {
                    setProgress({
                        pct: typeof data.progress === 'number' ? data.progress : 0,
                        step: data.current_step || 'Working…',
                        items: data.items_found || 0,
                        list: Array.isArray(data.partial_items) ? data.partial_items : [],
                    });
                }
            } catch (err) {
                // transient network blip during polling — keep trying
            }
        }
        setError('The AI is taking longer than expected. Please try again in a moment.');
        setStage('input');
    };

    const updateItem = (id, patch) => {
        setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
    };

    const selectedCount = useMemo(() => items.filter(i => i.selected).length, [items]);
    const dupCount = useMemo(() => items.filter(i => i.duplicate).length, [items]);

    // Group items by day for display
    const grouped = useMemo(() => {
        const g = {};
        items.forEach(it => {
            const d = it.day || 1;
            if (!g[d]) g[d] = [];
            g[d].push(it);
        });
        return Object.keys(g).map(Number).sort((a, b) => a - b).map(day => ({ day, list: g[day] }));
    }, [items]);

    const toggleInterest = (i) => {
        setInterests(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
    };

    const handleCommit = async () => {
        if (selectedCount === 0) {
            setError('Select at least one item to import.');
            return;
        }
        setError('');
        setStage('committing');
        try {
            const token = localStorage.getItem('token');
            const payload = {
                context: isAppend ? 'existing' : 'new',
                trip_id: tripId || undefined,
                trip_profile: {
                    name: tripName,
                    start_location: destination,
                    primary_destination: destination,
                    temporal_mode: result.temporal_mode,
                    start_date: result.start_date,
                    end_date: result.end_date,
                    duration_days: result.suggested_days,
                    pace,
                    interests,
                },
                items,
            };
            const resp = await fetch(`${API_BASE_URL}/ai/import_itinerary_commit.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            const data = await resp.json();
            if (!resp.ok || !data.success) {
                setError(data.message || 'Import failed.');
                setStage('review');
                return;
            }
            const newId = data.trip_id;
            const createdNew = data.created_new;
            resetAll();
            onComplete && onComplete(newId, createdNew);
        } catch (err) {
            setError('Network error while saving the import.');
            setStage('review');
        }
    };

    const modeInfo = result ? (MODE_LABEL[result.temporal_mode] || MODE_LABEL.ai_paced) : null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { bgcolor: DARK_BG, color: TEXT_PRIMARY, borderRadius: 3, border: `1px solid ${DARK_BORDER}` } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg,#6C63FF,#a855f7)' }}>
                    <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
                        {isAppend ? 'AI Import — Add to Trip' : 'AI Itinerary Import'}
                    </Typography>
                    {isAppend && existingTripName && (
                        <Typography sx={{ color: TEXT_MUTED, fontSize: '0.78rem' }}>
                            Appending to “{existingTripName}”
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8, color: TEXT_MUTED }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: DARK_BORDER }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* ── INPUT STAGE ── */}
                {stage === 'input' && (
                    <Box>
                        <Typography sx={{ color: TEXT_MUTED, fontSize: '0.88rem', mb: 1.5 }}>
                            Paste a raw itinerary, booking email, or a list of attractions. The AI will
                            extract flights, lodging and activities, then let you review everything before saving.
                        </Typography>
                        <TextField
                            value={rawText}
                            onChange={(e) => setRawText(e.target.value)}
                            placeholder="e.g. Colosseum, Vatican Museums, Trevi Fountain... or paste a flight confirmation email"
                            multiline
                            minRows={8}
                            fullWidth
                            sx={fieldSx}
                        />
                        <Box sx={{ mt: 1.5 }}>
                            <input ref={fileRef} type="file" accept=".txt,.eml,.ics,.csv,.md,text/plain" hidden onChange={handleFile} />
                            <Button
                                onClick={() => fileRef.current && fileRef.current.click()}
                                startIcon={<UploadFileIcon />}
                                size="small"
                                sx={{ color: TEXT_MUTED, textTransform: 'none' }}
                            >
                                Upload a text file
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* ── COMMITTING STAGE ── */}
                {stage === 'committing' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                        <Typography sx={{ color: TEXT_MUTED }}>Saving your trip…</Typography>
                    </Box>
                )}

                {/* ── LOADING STAGE — live background progress ── */}
                {stage === 'loading' && (
                    <Box sx={{ py: 3, px: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <CircularProgress size={22} sx={{ color: ACCENT }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: '0.95rem' }}>
                                    {progress.step || 'Analyzing your itinerary…'}
                                </Typography>
                                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.8rem' }}>
                                    {progress.items > 0
                                        ? `${progress.items} item${progress.items > 1 ? 's' : ''} found so far · elapsed ${fmtElapsed(elapsed)}`
                                        : `Working in the background · elapsed ${fmtElapsed(elapsed)}`}
                                </Typography>
                            </Box>
                            <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: '0.9rem', minWidth: 40, textAlign: 'right' }}>
                                {progress.pct || 0}%
                            </Typography>
                        </Box>

                        {/* Progress bar */}
                        <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                            <Box sx={{
                                height: '100%',
                                width: `${Math.max(4, progress.pct || 0)}%`,
                                borderRadius: 4,
                                background: 'linear-gradient(90deg,#6C63FF,#a855f7)',
                                transition: 'width 0.5s ease',
                            }} />
                        </Box>

                        {/* Persistent "still working" reassurance */}
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(108,99,255,0.10)', border: `1px solid rgba(108,99,255,0.25)`, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                            <AutoAwesomeIcon sx={{ color: ACCENT, fontSize: 18, mt: '1px', flexShrink: 0 }} />
                            <Typography sx={{ color: TEXT_PRIMARY, fontSize: '0.82rem', lineHeight: 1.5 }}>
                                The AI is reading your itinerary and building it <b>one day at a time</b> so nothing gets dropped.
                                This can take a few minutes for long trips — <b>please keep this window open</b>; it keeps
                                working in the background and will show your trip here when it's done.
                            </Typography>
                        </Box>

                        {/* Live list of items as they're discovered */}
                        {progress.list && progress.list.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
                                    Found so far
                                </Typography>
                                <Box sx={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5, '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(-2px)' }, to: { opacity: 1, transform: 'none' } } }}>
                                    {progress.list.map((it, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: DARK_SURFACE, borderRadius: 1.5, px: 1, py: 0.75, animation: 'fadeIn 0.3s ease' }}>
                                            {typeIcon(it)}
                                            <Typography sx={{ color: TEXT_PRIMARY, fontSize: '0.82rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {it.title}
                                            </Typography>
                                            <Typography sx={{ color: TEXT_MUTED, fontSize: '0.72rem', flexShrink: 0 }}>
                                                Day {it.day}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* ── REVIEW / VERIFICATION STAGE ── */}
                {stage === 'review' && result && (
                    <Box>
                        {/* Trip profile summary */}
                        {!isAppend && (
                            <Box sx={{ bgcolor: DARK_SURFACE, borderRadius: 2, p: 2, mb: 2, border: `1px solid ${DARK_BORDER}` }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                                    <Chip
                                        size="small"
                                        label={`${modeInfo.label} · ${result.suggested_days} day${result.suggested_days > 1 ? 's' : ''}`}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: modeInfo.color, fontWeight: 600 }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                    <TextField label="Trip name" value={tripName} onChange={(e) => setTripName(e.target.value)} size="small" sx={{ ...fieldSx, flex: '1 1 200px' }} />
                                    <TextField label="Destination" value={destination} onChange={(e) => setDestination(e.target.value)} size="small" sx={{ ...fieldSx, flex: '1 1 200px' }} />
                                </Box>

                                {/* Psychographic tagging */}
                                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.75rem', mt: 2, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Pace</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {PACE_OPTIONS.map(p => (
                                        <Chip key={p} label={p} size="small" onClick={() => setPace(p)}
                                            sx={{ cursor: 'pointer', bgcolor: pace === p ? ACCENT : 'rgba(255,255,255,0.06)', color: pace === p ? '#fff' : TEXT_MUTED, fontWeight: 600 }} />
                                    ))}
                                </Box>
                                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.75rem', mt: 1.5, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Interests</Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    {INTEREST_OPTIONS.map(i => (
                                        <Chip key={i} label={i} size="small" onClick={() => toggleInterest(i)}
                                            sx={{ cursor: 'pointer', bgcolor: interests.includes(i) ? 'rgba(108,99,255,0.35)' : 'rgba(255,255,255,0.06)', color: interests.includes(i) ? '#fff' : TEXT_MUTED, border: interests.includes(i) ? `1px solid ${ACCENT}` : '1px solid transparent' }} />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Typography sx={{ fontSize: '0.8rem', color: TEXT_MUTED, mb: 1 }}>
                            {selectedCount} of {items.length} item{items.length > 1 ? 's' : ''} selected — uncheck anything you don't want, edit any field inline.
                            {dupCount > 0 && (
                                <Box component="span" sx={{ color: '#fbbf24', display: 'block', mt: 0.25 }}>
                                    {dupCount} item{dupCount > 1 ? 's are' : ' is'} already in your trip and won't be re-added (unchecked below).
                                </Box>
                            )}
                        </Typography>

                        {/* Day-grouped staging list */}
                        {grouped.map(({ day, list }) => (
                            <Box key={day} sx={{ mb: 1.5 }}>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: ACCENT, mb: 0.5 }}>
                                    Day {day}
                                </Typography>
                                {list.map(item => (
                                    <Box key={item.id} sx={{ bgcolor: DARK_SURFACE, borderRadius: 2, mb: 1, border: `1px solid ${item.selected ? 'rgba(108,99,255,0.4)' : DARK_BORDER}`, opacity: item.selected ? 1 : 0.55 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                                            <Checkbox
                                                checked={!!item.selected}
                                                onChange={(e) => updateItem(item.id, { selected: e.target.checked })}
                                                sx={{ color: TEXT_MUTED, '&.Mui-checked': { color: ACCENT }, p: 0.5 }}
                                            />
                                            {typeIcon(item)}
                                            <TextField
                                                value={item.title}
                                                onChange={(e) => updateItem(item.id, { title: e.target.value })}
                                                variant="standard"
                                                fullWidth
                                                InputProps={{ disableUnderline: true, sx: { color: TEXT_PRIMARY, fontWeight: 600, fontSize: '0.9rem' } }}
                                            />
                                            {item.duplicate && (
                                                <Chip size="small" label="Already in trip"
                                                    sx={{ height: 20, fontSize: '0.66rem', bgcolor: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.4)', flexShrink: 0 }} />
                                            )}
                                            {item.start_time && (
                                                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{item.start_time}</Typography>
                                            )}
                                            <IconButton size="small" onClick={() => setExpanded(p => ({ ...p, [item.id]: !p[item.id] }))} sx={{ color: TEXT_MUTED }}>
                                                {expanded[item.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </IconButton>
                                        </Box>

                                        <Collapse in={!!expanded[item.id]}>
                                            <Divider sx={{ borderColor: DARK_BORDER }} />
                                            <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                <TextField label="Day" type="number" value={item.day} onChange={(e) => updateItem(item.id, { day: Math.max(1, parseInt(e.target.value || '1', 10)) })} size="small" sx={{ ...fieldSx, width: 80 }} />
                                                <TextField label="Start time" value={item.start_time} onChange={(e) => updateItem(item.id, { start_time: e.target.value })} placeholder="HH:MM" size="small" sx={{ ...fieldSx, width: 110 }} />
                                                {item.type === 'activity' && (
                                                    <TextField label="Duration (min)" type="number" value={item.duration} onChange={(e) => updateItem(item.id, { duration: parseInt(e.target.value || '0', 10) })} size="small" sx={{ ...fieldSx, width: 130 }} />
                                                )}
                                                <TextField label="Location" value={item.location} onChange={(e) => updateItem(item.id, { location: e.target.value })} size="small" sx={{ ...fieldSx, flex: '1 1 200px' }} />
                                                {item.type === 'transportation' && (
                                                    <>
                                                        <TextField label="Carrier" value={item.carrier_name} onChange={(e) => updateItem(item.id, { carrier_name: e.target.value })} size="small" sx={{ ...fieldSx, flex: '1 1 140px' }} />
                                                        <TextField label="Flight / number" value={item.flight_number} onChange={(e) => updateItem(item.id, { flight_number: e.target.value })} size="small" sx={{ ...fieldSx, width: 130 }} />
                                                        <TextField label="From" value={item.origin_airport_code || item.origin_city} onChange={(e) => updateItem(item.id, { origin_airport_code: e.target.value })} size="small" sx={{ ...fieldSx, width: 100 }} />
                                                        <TextField label="To" value={item.destination_airport_code || item.destination_city} onChange={(e) => updateItem(item.id, { destination_airport_code: e.target.value })} size="small" sx={{ ...fieldSx, width: 100 }} />
                                                        <TextField label="Seat" value={item.seat_number} onChange={(e) => updateItem(item.id, { seat_number: e.target.value })} size="small" sx={{ ...fieldSx, width: 90 }} />
                                                    </>
                                                )}
                                                <TextField label="Confirmation #" value={item.reference_ticket_no} onChange={(e) => updateItem(item.id, { reference_ticket_no: e.target.value })} size="small" sx={{ ...fieldSx, flex: '1 1 140px' }} />
                                                <TextField label="Booked via" value={item.booking_agency} onChange={(e) => updateItem(item.id, { booking_agency: e.target.value })} size="small" sx={{ ...fieldSx, flex: '1 1 140px' }} />
                                                <TextField label="Notes" value={item.notes} onChange={(e) => updateItem(item.id, { notes: e.target.value })} size="small" fullWidth multiline sx={fieldSx} />
                                            </Box>
                                        </Collapse>
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                {stage === 'input' && (
                    <>
                        <Button onClick={handleClose} sx={{ color: TEXT_MUTED, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleParse} variant="contained" startIcon={<AutoAwesomeIcon />}
                            sx={{ textTransform: 'none', background: 'linear-gradient(135deg,#6C63FF,#a855f7)', fontWeight: 700 }}>
                            Analyze with AI
                        </Button>
                    </>
                )}
                {stage === 'review' && (
                    <>
                        <Button onClick={handleClose} sx={{ color: TEXT_MUTED, textTransform: 'none' }}>Cancel</Button>
                        <Tooltip title={selectedCount === 0 ? 'Select at least one item' : ''}>
                            <span>
                                <Button onClick={handleCommit} variant="contained" disabled={selectedCount === 0}
                                    sx={{ textTransform: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', fontWeight: 700 }}>
                                    Confirm &amp; Apply ({selectedCount})
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
